/**
 * HTTP Client Module
 * Replaces: HttpClient with AutomaticDecompression, custom headers
 * Original: Form5.cs HttpClient setup, AddinModule.cs HttpClient setup
 */

import { config } from '../../utils/config';
import { createLogger, rowsOf } from '../../utils/logger';
import { tokenStorage } from '../auth/tokenStorage';
import { WAF_BLOCKED_MESSAGE, wafAction } from '../../utils/waf';
import { SESSION_ENDED_MESSAGE, isSessionEnded, notifySessionEnded } from '../../utils/sessionEnded';

const logger = createLogger('HttpClient');

export interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: Record<string, string>;
  body?: any;
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  skipAuth?: boolean;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
  headers: Headers;
  requestId?: string;
}

export class HttpClient {
  private baseUrl: string;
  private username: string;
  private password: string;
  private defaultTimeout: number;
  private maxRetries: number;
  // VAPT V-001: same-origin proxied clients carry a non-secret credential marker
  // (dd/bgf/contact) instead of a Basic credential. nginx maps the marker to the
  // real backend credential (held server-side) after verifying the edge token.
  private credMarker?: string;

  constructor(
    baseUrl: string,
    username: string,
    password: string,
    options: { timeout?: number; maxRetries?: number; credMarker?: string } = {}
  ) {
    // Strip EVERY trailing slash, so a base mistyped with '//' in .env still
    // joins cleanly. The proxy base '/' becomes '' and requests stay '/path'.
    this.baseUrl = baseUrl.replace(/\/+$/, '');
    this.username = username;
    this.password = password;
    this.defaultTimeout = options.timeout || config.HTTP_TIMEOUT_MINUTES * 60 * 1000;
    this.maxRetries = options.maxRetries || 1;
    this.credMarker = options.credMarker;
  }

  /**
   * Create Basic Auth header
   * Equivalent to: Convert.ToBase64String(Encoding.ASCII.GetBytes($"{username}:{password}"))
   */
  private getAuthHeader(): string {
    const credentials = btoa(`${this.username}:${this.password}`);
    return `Basic ${credentials}`;
  }

  /**
   * Make HTTP request with retry logic
   * Equivalent to: HttpClient.GetAsync(), PostAsync() with ServicePointManager settings
   */
  async request<T>(endpoint: string, config_: RequestConfig = {}): Promise<ApiResponse<T>> {
    // Handle proxy case: baseUrl is '/' so we need to avoid double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    const url = this.baseUrl === '/' 
      ? `/${cleanEndpoint}`
      :  this.baseUrl === config.ISTARI_NEW_PROXY_URL
        ? `${this.baseUrl}${cleanEndpoint}`
        : `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    const method = config_.method || 'GET';
    const timeout = config_.timeout || this.defaultTimeout;
    const retries = config_.retries ?? this.maxRetries;
    const retryDelay = config_.retryDelay || 1000;
    
    const requestId = Math.random().toString(36).substring(2, 10);
    
    // Only the body is passed: apiRequest logs its SIZE (req_bytes), never its
    // content. Wrapping it in a descriptor object made every request - even a
    // body-less GET - report the descriptor's ~53 bytes.
    logger.apiRequest(method, url, config_.body);

    // Build headers
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Accept-Encoding': 'gzip, deflate',
      'User-Agent': config.USER_AGENT || 'InsyncAddinRequester',
      ...config_.headers
    };

    if (!config_.skipAuth) {
      if (this.credMarker) {
        // VAPT V-001: no Basic credential in the browser. Send the non-secret
        // marker (which backend credential nginx should attach) plus the edge
        // session token minted at login; nginx verifies the token and injects the
        // real credential server-side.
        headers['X-Insync-Auth'] = this.credMarker;
        if (url.startsWith('/')) {
          const edgeToken = await resolveEdgeToken();
          if (edgeToken) headers['X-Insync-Token'] = edgeToken;
        }
      } else {
        headers.Authorization = this.getAuthHeader();
      }
    }

    // Tag same-origin (proxied) requests with the logged-in username so nginx can
    // attribute each API call to a user (logged as `user_email`). Relative URLs
    // only — a custom header on a cross-origin request would trigger a CORS
    // preflight. Never blocks the request: reads the sync cache first, hydrating
    // once from storage after a reload. Header-injection-safe (strips CR/LF).
    if (url.startsWith('/') && !headers['X-Insync-User']) {
      const currentUser = await resolveInsyncUser();
      if (currentUser) {
        headers['X-Insync-User'] = currentUser;
      }
    }

    if (config_.body && typeof config_.body === 'object' && !(config_.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      // Declared out here so the catch below can report how long the failing
      // attempt took - a timeout that burns 60s and one that fails instantly
      // otherwise look identical in the logs.
      const attemptStartedAt = Date.now();
      try {
        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const fetchConfig: RequestInit = {
          method,
          headers,
          signal: controller.signal
        };

        if (config_.body) {
          fetchConfig.body = typeof config_.body === 'string' 
            ? config_.body 
            : JSON.stringify(config_.body);
        }

        const response = await fetch(url, fetchConfig);
        const elapsedMs = Date.now() - attemptStartedAt;
        clearTimeout(timeoutId);

        // Extract X-Request-ID if present (for debugging)
        const requestId = response.headers.get('X-Request-ID') || undefined;

        // AWS WAF answered instead of the backend (CAPTCHA/Challenge). Fail with
        // a user-facing message and never retry: the body is a challenge page we
        // must not parse or show, and retrying only feeds a rate-based rule.
        const waf = wafAction(response);
        if (waf) {
          throw new WafBlockedError(response.status, waf, requestId);
        }

        // The edge refused the user's session (ended server-side). Tell the app
        // so it logs out locally and shows the login screen; never retried.
        if (isSessionEnded(response)) {
          notifySessionEnded();
          throw new HttpError(SESSION_ENDED_MESSAGE, 401, requestId, false);
        }

        // Session warm-up: some istari endpoints (e.g. addin-model-download)
        // need a session cookie that is only set on first contact. A cold
        // request 302-redirects to "/session-expired/" (which sets the cookie),
        // and fetch follows that to a 404. The cookie now exists, so the same
        // request succeeds on retry. Mark retryable so the loop re-attempts it
        // with the freshly-established session instead of surfacing a 404.
        if (isSessionExpiredRedirect(response)) {
          throw new HttpError(
            'Session not yet established (redirected to session-expired); retrying',
            response.status,
            requestId,
            true
          );
        }

        if (!response.ok) {
          throw new HttpError(
            `HTTP ${response.status}: ${response.statusText}`,
            response.status,
            requestId
          );
        }

        // Parse JSON response - handle NaN/Infinity which are invalid JSON
        const text = await response.text();

        // A 200 with an HTML body means the request never reached the JSON API
        // (proxy fell through to the SPA/taskpane.html, or upstream returned a
        // login/WAF/error page). Fail with a clear message instead of a cryptic
        // "Unexpected token '<'" from JSON.parse. Not retryable — the body won't
        // change on retry.
        const contentType = response.headers.get('Content-Type') || '';
        if (/^\s*<(?:!doctype|html)/i.test(text)) {
          throw new HttpError(
            `Expected JSON but received an HTML page (Content-Type: ${contentType || 'unknown'}). ` +
              `This usually means the request was not routed to the API. First bytes: ` +
              `${text.slice(0, 80).replace(/\s+/g, ' ')}`,
            response.status,
            requestId,
            false
          );
        }

        let data: T;
        try {
          const sanitizedText = text
            .replace(/\bNaN\b/g, 'null')
            .replace(/\bInfinity\b/g, 'null')
            .replace(/\b-Infinity\b/g, 'null');
          data = JSON.parse(sanitizedText);
        } catch (parseErr) {
          throw new HttpError(
            `Failed to parse response as JSON: ${(parseErr as Error).message}. ` +
              `First bytes: ${text.slice(0, 80).replace(/\s+/g, ' ')}`,
            response.status,
            requestId,
            false
          );
        }

        logger.apiResponse(
          method,
          url,
          response.status,
          {
            requestId,
            hasData: !!data,
            dataType: typeof data,
            isArray: Array.isArray(data),
          },
          elapsedMs,
          // Measured on the real body: decoded characters, which for JSON is
          // the uncompressed size in bytes. Never the metadata object above.
          { bytes: text.length, rows: rowsOf(data) }
        );

        return {
          data,
          status: response.status,
          headers: response.headers,
          requestId
        };

      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        logger.apiError(
          method,
          url,
          lastError,
          (error as HttpError)?.status,
          Date.now() - attemptStartedAt
        );
        
        // Don't retry when the server responded but the body wasn't valid JSON
        // (HTML/login/error page) — the same request yields the same body.
        if (error instanceof HttpError && error.retryable === false) {
          logger.warn(`Non-retryable response - not retrying`, { url, requestId });
          throw error;
        }

        // Don't retry on 4xx errors (client errors) — unless explicitly marked
        // retryable (e.g. the session warm-up redirect, which succeeds once the
        // session cookie set by this request is sent on the next attempt).
        if (
          error instanceof HttpError &&
          error.retryable !== true &&
          error.status >= 400 &&
          error.status < 500
        ) {
          logger.warn(`HTTP ${error.status} error - not retrying`, { url, requestId });
          throw error;
        }

        // Don't retry on abort (timeout)
        if (error instanceof DOMException && error.name === 'AbortError') {
          logger.error(`Request timeout after ${timeout}ms`, lastError, { url, requestId });
          throw new Error(`Request timeout after ${timeout}ms`);
        }

        // Wait before retry
        if (attempt < retries) {
          logger.info(`Retrying request (${attempt + 1}/${retries})`, { url, requestId, delay: retryDelay * Math.pow(2, attempt) });
          await delay(retryDelay * Math.pow(2, attempt)); // Exponential backoff
        }
      }
    }

    throw lastError || new Error('Request failed after retries');
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, config_: Omit<RequestConfig, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config_, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, config_: Omit<RequestConfig, 'method'> = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...config_, method: 'POST' });
  }
}

/**
 * HTTP Error with status code
 */
export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public requestId?: string,
    // false = the request completed but the response is unusable (e.g. HTML
    // instead of JSON); retrying won't help. Undefined keeps default behavior.
    public retryable?: boolean
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/**
 * The request was intercepted by AWS WAF (see utils/waf.ts). Non-retryable, and
 * its message is safe to show to the user as-is - every pane displays
 * error.message, so this is what they read.
 */
export class WafBlockedError extends HttpError {
  constructor(status: number, public wafAction: string, requestId?: string) {
    super(WAF_BLOCKED_MESSAGE, status, requestId, false);
    this.name = 'WafBlockedError';
  }
}

/**
 * Delay utility
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function isSessionExpiredRedirect(response: Response): boolean {
  return response.redirected && /session-expired/i.test(response.url);
}

/**
 * fetch() wrapper that transparently handles the istari session warm-up
 * described in isSessionExpiredRedirect(). Use this for raw (non-JSON) requests
 * such as binary file downloads that don't go through HttpClient. Each host has
 * its own session, so the first call to a new host warms it up here.
 */
export async function fetchWithSessionWarmup(
  input: RequestInfo | URL,
  init?: RequestInit,
  maxRetries = 1
): Promise<Response> {
  // Attribute same-origin (proxied) requests to the logged-in user so nginx logs
  // them with user_email (Grafana User Logs Dashboard). Relative URLs only — a
  // custom header on a cross-origin request would trigger a CORS preflight.
  let effectiveInit = init;
  if (typeof input === 'string' && input.startsWith('/')) {
    const headers = new Headers(init?.headers);
    if (!headers.has('X-Insync-User')) {
      const user = await resolveInsyncUser();
      if (user) headers.set('X-Insync-User', user);
    }
    effectiveInit = { ...init, headers };
  }

  let response = await fetch(input, effectiveInit);
  for (let attempt = 0; isSessionExpiredRedirect(response) && attempt < maxRetries; attempt++) {
    await delay(500 * Math.pow(2, attempt)); // Exponential backoff
    response = await fetch(input, effectiveInit);
  }
  // Callers download files from this response; a WAF challenge page would be
  // saved/parsed as the file. Fail instead, without reading the body.
  const waf = wafAction(response);
  if (waf) {
    throw new WafBlockedError(response.status, waf, response.headers.get('X-Request-ID') || undefined);
  }
  if (isSessionEnded(response)) {
    notifySessionEnded();
    throw new HttpError(SESSION_ENDED_MESSAGE, 401, response.headers.get('X-Request-ID') || undefined, false);
  }
  return response;
}

export async function resolveInsyncUser(): Promise<string | null> {
  const currentUser = tokenStorage.getCachedUsername() ?? (await tokenStorage.ensureUsername());
  return currentUser ? currentUser.replace(/[\r\n]/g, '').slice(0, 320) : null;
}

/**
 * The edge session token (VAPT V-001) for the X-Insync-Token request header.
 * Reads the sync cache first, hydrating once from storage (incl. the localStorage
 * mirror so the custom-functions runtime can read it). CR/LF-stripped. Returns
 * null before login / until hydrated — the request then 401s and the user is
 * prompted to re-authenticate.
 */
export async function resolveEdgeToken(): Promise<string | null> {
  const token = tokenStorage.getCachedEdgeToken() ?? (await tokenStorage.getEdgeToken());
  return token ? token.replace(/[\r\n]/g, '') : null;
}

// Consensus + batch APIs go through our OWN origin (nginx proxies /consensus*,
// /consensus_v2*, /data-delivery* -> data-deliveryv2) so the browser calls
// same-origin and never depends on that host's CORS. Always relative.
const dataDeliveryBaseUrl = config.DATA_DELIVERY_PROXY_URL;

const istariNewBaseUrl = process.env.NODE_ENV === 'development'
  ? config.ISTARI_NEW_PROXY_URL
  : config.ISTARI_NEW_BASE_URL;

const istariBaseUrl = config.ISTARI_PROXY_URL;

const legacyBaseUrl = config.LEGACY_PROXY_URL;

export const dataDeliveryClient = new HttpClient(
  dataDeliveryBaseUrl,
  '',
  '',
  { timeout: 10 * 60 * 1000, credMarker: 'dd' } // 10 minutes
);

export const backgroundFetcherClient = new HttpClient(
  dataDeliveryBaseUrl,
  '',
  '',
  { timeout: 10 * 60 * 1000, credMarker: 'dd' }
);

export const istariNewClient = new HttpClient(
  istariNewBaseUrl,
  '',
  '',
  { timeout: 2 * 60 * 1000,  }
);

export const istariClient = new HttpClient(
  istariBaseUrl,
  '',
  '',
  { timeout: 2 * 60 * 1000,  }
);

export const legacyClient = new HttpClient(
  legacyBaseUrl,
  '',
  '',
  { timeout: 10 * 60 * 1000,  }
);

export default HttpClient;
